// Tender ("Payment Type") filter for the Orders list.
//
// reports_transactions.payment_type is a free-text column, not a Hasura enum,
// so the raw spelling of each tender is not guaranteed by the schema. The UI
// therefore offers stable canonical keys and this module expands each one into
// the raw values it is known to appear as.
//
// Matching is exact-but-case-insensitive: each candidate becomes an _ilike with
// NO wildcards. Substring matching would be wrong here — "%cash%" also matches
// "cash_balance", which is a different tender with its own row in every
// financial report.
export const PAYMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "credit", label: "Credit" },
  { value: "cash", label: "Cash" },
  { value: "rfid", label: "RFID" },
  { value: "qr", label: "QR Tap" },
  { value: "cash_balance", label: "Cash Balance" },
  { value: "promo_balance", label: "Promo Balance" },
  { value: "token", label: "Tokens" },
  { value: "tappit", label: "Tappit" },
];

// Canonical key -> every raw payment_type spelling that means the same tender.
// The first entry is the spelling we believe production writes; the rest are
// defensive alternates so a filter never silently returns zero rows because the
// POS wrote "qr_code" where we guessed "qr".
export const PAYMENT_TYPE_SYNONYMS: Record<string, string[]> = {
  credit: ["credit", "card"],
  cash: ["cash"],
  rfid: ["rfid"],
  qr: ["qr", "qr_code", "qrcode", "qr_tap", "qrtap"],
  cash_balance: ["cash_balance", "cashbalance"],
  promo_balance: ["promo_balance", "promobalance"],
  token: ["token", "tokens"],
  tappit: ["tappit"],
};

// A QR sale is an RFID sale to the API and to every report proc: the POS
// reads the attendee's QR code and charges the same cashless balances, and
// payments.payment_type stays "rfid". The only record of the QR read is
// scan_method on each leg of orders.cashless_charges (posapp-s700 build 479+),
// reached from a transaction row through order_payment.
const QR_SCAN_CLAUSE = {
  order_payment: { cashless_charges: { _contains: [{ scan_method: "qr" }] } },
};

export const isQrScan = (cashlessCharges: unknown): boolean =>
  Array.isArray(cashlessCharges) &&
  cashlessCharges.some((leg) => leg && typeof leg === "object" && (leg as { scan_method?: string }).scan_method === "qr");

/** The tender to show for a row: "qr" for an RFID sale that was read by QR code. */
export const displayPaymentType = (paymentType: unknown, cashlessCharges: unknown): string => {
  const raw = paymentType == null ? "" : String(paymentType);
  return raw.toLowerCase() === "rfid" && isQrScan(cashlessCharges) ? "qr" : raw;
};

/**
 * Expands the canonical keys picked in the UI into a Hasura _or group over
 * payment_type. Returns [] when nothing is selected, so the caller can skip
 * adding a condition entirely.
 *
 * "qr" also matches rfid rows whose legs were read by QR code, and "rfid"
 * excludes those, so the two keys split the cashless sales between them.
 */
export const buildPaymentTypeOr = (canonicalKeys: string[]) => {
  const keys = canonicalKeys
    .map((key) => (key ?? "").trim())
    .filter((key) => key.length > 0);
  const wantsQr = keys.includes("qr");
  const wantsRfid = keys.includes("rfid");

  const raw = keys.flatMap((key) => PAYMENT_TYPE_SYNONYMS[key] ?? [key]);

  const clauses: Record<string, unknown>[] = Array.from(new Set(raw)).map((value) =>
    value === "rfid" && !wantsQr
      ? { payment_type: { _ilike: value }, _not: QR_SCAN_CLAUSE }
      : { payment_type: { _ilike: value } },
  );

  if (wantsQr && !wantsRfid) {
    clauses.push({ payment_type: { _ilike: "rfid" }, ...QR_SCAN_CLAUSE });
  }

  return clauses;
};
